import React, { useEffect } from 'react';
import { View } from 'react-native';
import { Tabs, useRouter } from 'expo-router';
import { Home, BarChart3, FolderOpen, MessageCircle } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { usePhoneShell } from '@/lib/useShell';
import { track } from '@/lib/analytics';
import { useAuth } from '@/lib/supabase';
import { useProfileStore } from '@/lib/state/profile-store';
import { useUserRole } from '@/lib/auth/role';
import { resolveGuardState } from '@/lib/auth/guard-state';
import { useAALGate } from '@/lib/auth/aal';
import { AuthGuardError } from '@/components/AuthGuardError';
import { mode } from '@/lib/config/app-mode';
import { COMMERCE_ENABLED } from '@/lib/commerce/policy';
import { useUnreadMessageCount } from '@/lib/messaging/use-unread-messages';

export default function StartupTabLayout() {
  const { user, isLoading: isAuthLoading } = useAuth();
  // Persisted store used only in mock mode; production reads server-fetched profiles.role
  // via useUserRole. This prevents devtools-edits of localStorage from unlocking the
  // startup shell (SEC-FE-02 / RM-T1-18).
  const persistedRole = useProfileStore((s) => s.userRole);
  const { data: serverRole, isLoading: isRoleLoading, isError: isRoleError, refetch: refetchRole } = useUserRole(user?.id);
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();

  // Wait for auth + server role fetch to finish before checking authorization to avoid
  // navigating before the Root Layout mounts (Expo Router race condition).
  const isLoading = isAuthLoading || isRoleLoading;
  const hasAuth = !!user || mode.isMock;
  const effectiveRole = mode.isMock ? persistedRole : serverRole;
  const isAuthorized = hasAuth && effectiveRole === 'startup';
  // AAL2 boundary (SEC-AUTH-02c): password login mints a REAL AAL1 session
  // before MFA is verified, so role alone is not enough — an enrolled user who
  // skipped /mfa-verify was previously inside the app on an AAL1 token. Users
  // with no MFA factor report 'satisfied' and are unaffected.
  const { status: aal, retry: retryAal } = useAALGate();
  const guardState = resolveGuardState({ isLoading, isError: isRoleError, isAuthorized, aal });

  // Unread badge on the Messages tab. It belongs HERE rather than in the
  // messages screen because a badge earns its keep precisely when you are not
  // on that screen. Shares the screen's React Query cache entry — same key —
  // so this adds no second request. Called before the early returns below, per
  // the rules of hooks.
  const unreadMessages = useUnreadMessageCount('startup');

  useEffect(() => {
    if (guardState === 'redirect') {
      // Authenticated but not entitled to THIS group → '/', which re-routes to
      // the group they do belong to. Sending a live session to /login bounces it
      // straight back and, where the router and the guard disagree about the
      // group, loops (see (admin)/_layout.tsx). Only signed-out visitors go to
      // /login.
      router.replace(hasAuth ? '/' : '/login');
    } else if (guardState === 'step-up-required') {
      // Enrolled but still AAL1. Send them to complete the step-up rather than
      // to /login — they have already proven their password, and bouncing them
      // to a fresh sign-in would just mint another AAL1 session and loop.
      router.replace('/mfa-verify');
    }
  }, [guardState, hasAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Transient role-fetch error → offer a retry instead of bouncing the user out.
  // Retry must re-run BOTH probes. The AAL probe is a separate React Query
  // entry, so refetching only the role left a failed AAL result stale — the
  // guard kept deriving 'error' from it and the screen could never recover.
  if (guardState === 'error') {
    return <AuthGuardError onRetry={() => { refetchRole(); retryAal(); }} />;
  }
  if (guardState !== 'authorized') return <View style={{ flex: 1 }} />;
  return (
    <Tabs
      screenListeners={{
        tabPress: (e) => {
          track('tab_switch', { tab: e.target?.split('-')[0] ?? 'unknown', group: 'startup' });
        },
      }}
      screenOptions={{
        headerShown: false,
        // The tab bar is the navigation owner for every PHONE-SHAPED viewport —
        // native and phone web alike — and yields to the sidebar above the tablet
        // breakpoint. It used to key off `isWeb`, which hid it at every web width
        // while the sidebar only mounts at >= 768px, so phone web had no
        // navigation at all. Reusing this same navigator (rather than building a
        // parallel web nav) is what keeps `Tabs.Protected` gating, `href: null`
        // hiding and the unread badges identical on both platforms.
        tabBarStyle: phoneShell
          ? {
              backgroundColor: colors.surface.primary,
              borderTopColor: colors.border.light,
              borderTopWidth: 1,
              height: 85,
              paddingTop: 8,
            }
          : { display: 'none' },
        tabBarActiveTintColor: colors.primary[600],
        tabBarInactiveTintColor: colors.light[500],
        tabBarLabelStyle: {
          fontSize: 11,
          fontWeight: '600',
          marginTop: 4,
        },
      }}
    >
      <Tabs.Screen
        name="startup-dashboard"
        options={{
          title: 'Home',
          tabBarIcon: ({ color, size }) => <Home size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="score"
        options={{
          title: 'Score',
          tabBarIcon: ({ color, size }) => <BarChart3 size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="dataroom"
        options={{
          title: 'Data Room',
          tabBarIcon: ({ color, size }) => <FolderOpen size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="startup-messages"
        options={{
          title: 'Messages',
          tabBarIcon: ({ color, size }) => <MessageCircle size={size} color={color} />,
          // undefined, not 0 — React Navigation renders a badge for any defined
          // value, so 0 would pin an empty dot to the icon permanently.
          tabBarBadge: unreadMessages > 0 ? unreadMessages : undefined,
        }}
      />
      {/* Hidden routes — href: null hides from tab bar on all platforms.
         Routes remain navigable via router.push(). */}
      {/* PROFILE IS NOT A TAB. It is reached from the avatar at the top-right of
         the page header (`ScreenFrame.GlobalActions`), in both personas, which
         is where an account destination is looked for and where it stops
         competing with the four things a founder actually works in. The route is
         unchanged and still the single account destination: settings, security,
         gated billing, help and sign out all live under it. */}
      <Tabs.Screen name="userprofile" options={{ href: null }} />
      <Tabs.Screen name="strategic-analysis" options={{ href: null }} />
      <Tabs.Screen name="insights" options={{ href: null }} />
      <Tabs.Screen name="interview" options={{ href: null }} />
      <Tabs.Screen name="recommendations" options={{ href: null }} />
      <Tabs.Screen name="company" options={{ href: null }} />
      <Tabs.Protected guard={COMMERCE_ENABLED}>
        <Tabs.Screen name="startup-subscription" options={{ href: null }} />
      </Tabs.Protected>
      <Tabs.Screen name="help" options={{ href: null }} />
      <Tabs.Screen name="updates" options={{ href: null }} />
      <Tabs.Screen name="verification-requests" options={{ href: null }} />
      <Tabs.Screen name="shared-dd-reports" options={{ href: null }} />
      <Tabs.Screen name="dataroom-checklist" options={{ href: null }} />
      <Tabs.Screen name="valuation-wizard" options={{ href: null }} />
    </Tabs>
  );
}
